import {ApplicationRef, ChangeDetectorRef, Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {catchError, of, Subscription, take} from 'rxjs';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {DialogService, DynamicDialogConfig} from 'primeng/dynamicdialog';
import {FormBuilder, IMsfFormBuilder} from '@components/form-builder/form-builder';
import {FieldService, Season, SeasonService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {CROP_COLOR_MAP} from '@app/utils/_crop-colors';
import {OldMapComponent} from '@components/old-map/old-map.component';
import {DatePipe, DecimalPipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {CropCacheService} from '@services/caches/crop-cache.service';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {
  IdentificationCropSources,
  FieldSourceType
} from '@models/IFieldSource';
import {RegionService} from '@6grain/masofa-monolith-api-client/dictionaries';
import {
  CropIdentificationService
} from '@6grain/masofa-monolith-api-client/ai-inference/api/cropIdentification.service';
import {
  CropIdentificationExtendedDto
} from '@6grain/masofa-monolith-api-client/ai-inference/model/cropIdentificationExtendedDto';
import {
  CropIdentificationCandidateDto
} from '@6grain/masofa-monolith-api-client/ai-inference/model/cropIdentificationCandidateDto';
import {FilterOperator} from '@app/utils/filter-operator';
import {FieldPassportLinkComponent} from '@components/field-passport-link/field-passport-link.component';
import {PrimaryButton} from '@components/button/primary-button/primary-button';
import {BidService} from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import {BidWorkTypeService} from '@6grain/masofa-monolith-api-client/dictionaries/api/bidWorkType.service';
import {Crop} from '@6grain/masofa-monolith-api-client/dictionaries';
import {openCropIdentificationCreateRequestDialog} from '@app/pages/crop-identification/request/crop-identification-create-request.util';
import {
  AiInferenceImageDataSource
} from '@app/shared/data/ai-inference-image-data-source-uses-data';
import { LocalizeValuePipe } from '@app/utils/pipes/localize-value.pipe';

@Component({
  selector: 'app-modal-form-with-map',
  standalone: true,
  templateUrl: './modal-acceptance-identification.html',
  imports: [
    FormBuilder,
    TranslatePipe,
    OldMapComponent,
    FormsModule,
    DecimalPipe,
    FieldPassportLinkComponent,
    PrimaryButton,
    LocalizeValuePipe,
  ],
  providers: [DatePipe],
  styleUrls: ['./modal-acceptance-identification.scss']
})
export class ModalAcceptanceIdentification implements OnInit, OnDestroy {
  private regionManagerService: RegionManagerService = inject(RegionManagerService);
  private regionService: RegionService = inject(RegionService);
  private cropIdentificationService = inject(CropIdentificationService);
  private cdr = inject(ChangeDetectorRef);
  private appRef = inject(ApplicationRef);
  private langSub?: Subscription;
  protected readonly CROP_COLOR_MAP = CROP_COLOR_MAP;
  protected readonly fieldSourceType: FieldSourceType;
  /** Modal source list (no Agro portal). */
  protected readonly modalSourceOptions = IdentificationCropSources.filter(
    (opt) => opt.id !== FieldSourceType['Agro portal']
  );

  formData: Season;
  fieldArea: number = 0;
  canSave: boolean = true;
  datePipe = inject(DatePipe);

  /** Top candidates from DTO (with localized names). */
  topCandidates: Array<CropIdentificationCandidateDto> = [];
  /** Factual culture id — cloned before candidate selection overwrites `cropId`. */
  factCropId: string | null = null;
  /** 0 = first candidate, 1 = second (when present). */
  readonly selectedCandidateI = signal(0);

  readonly polygonStripeHex = computed(() => {
    const list = this.topCandidates;
    const idx = this.selectedCandidateI();
    const c = list[idx];
    const factId = this.factCropId;
    if (!c?.id || !factId) {
      return null as string | null;
    }
    if (String(c.id) === String(factId)) {
      return null;
    }
    return this.cropCacheService.getCropColorById(String(c.id), '#9c27b0');
  });

  constructor(
    public config: DynamicDialogConfig,
    private cropCacheService: CropCacheService,
    public translateService: TranslateService,
    private dialogService: DialogService,
    private bidService: BidService,
    private bidWorkTypeService: BidWorkTypeService,
    private fieldService: FieldService,
    private seasonService: SeasonService,
  ) {
    this.formData = config.data.model;
    this.fieldSourceType = config.data.type;
    this.captureFactCropId();
    config.data['fields'] = this.getEditForm();
  }

  ngOnInit(): void {
    this.langSub = this.translateService.onLangChange.subscribe(() => this.cdr.markForCheck());
    this.cropCacheService.getCrops().subscribe();
    const modelRecord = this.formData as Record<string, unknown>;
    if (modelRecord['source'] == null || modelRecord['source'] === '') {
      modelRecord['source'] = FieldSourceType.Satellite;
    }
    const regionFromMap = this.config.data?.regionId as string | null | undefined;
    if (regionFromMap) {
      (this.formData as { regionId?: string }).regionId = String(regionFromMap);
    }
    this.syncFieldAreaFromMapModel();
    this.syncDetectionDateFromMapModel();
    this.loadExtendedIdentification(!regionFromMap);
  }

  /** Value for `GetExtendedList` filter on field `id`. */
  private resolveIdentificationId(): string | null {
    const fromConfig = this.config.data?.aiResultId as string | null | undefined;
    if (fromConfig != null && String(fromConfig).trim() !== '') {
      return String(fromConfig);
    }
    const model = this.formData as Record<string, unknown>;
    const raw = model['aiResultId'] ?? model['id'];
    return raw != null && String(raw).trim() !== '' ? String(raw) : null;
  }

  private syncFieldAreaFromMapModel(): void {
    const area = (this.formData as Record<string, unknown>)['fieldArea'];
    if (area != null && !Number.isNaN(Number(area))) {
      this.fieldArea = Number(area);
      (this.formData as Record<string, unknown>)['fieldArea'] = this.fieldArea;
    }
  }

  private syncDetectionDateFromMapModel(): void {
    const model = this.formData as Record<string, unknown>;
    const raw = model['detectionDate'] ?? model['identificationDate'] ?? model['createAt'];
    this.applyDetectionDate(model, raw as string | Date | null | undefined);
  }

  private applyDetectionDate(
    model: Record<string, unknown>,
    raw: string | Date | null | undefined
  ): void {
    if (raw == null || raw === '') {
      return;
    }
    const date = raw instanceof Date ? raw : new Date(String(raw));
    if (!Number.isNaN(date.getTime())) {
      model['detectionDate'] = date;
    }
  }

  /** Load list row via GetExtendedList filtered by `id`. */
  private loadExtendedIdentification(fallbackRegionByPoint: boolean): void {
    const identificationId = this.resolveIdentificationId();
    if (!identificationId) {
      this.finishIdentificationLoad(fallbackRegionByPoint);
      return;
    }

    this.cropIdentificationService
      .aiInferenceCropIdentificationGetExtendedListPost({
        offset: 0,
        take: 1,
        filters: [
          {
            filterField: 'id',
            filterOperator: FilterOperator.Equals,
            filterValue: identificationId,
          },
        ],
      })
      .pipe(take(1), catchError(() => of([] as CropIdentificationExtendedDto[])))
      .subscribe((rows) => {
        const extended = Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
        if (extended) {
          this.applyExtendedDto(extended);
        }
        this.finishIdentificationLoad(fallbackRegionByPoint);
      });
  }

  private finishIdentificationLoad(fallbackRegionByPoint: boolean): void {
    const model = this.formData as { regionId?: string };
    if (fallbackRegionByPoint && !model.regionId) {
      this.loadRegionByPoint();
    }
    this.captureFactCropId();
    this.selectCandidateIndex(0);
    this.cdr.markForCheck();
  }

  /** Snapshot factual `cropId` before candidate tabs change the form value. */
  private captureFactCropId(): void {
    if (this.factCropId) {
      return;
    }
    const model = this.formData as Record<string, unknown>;
    const nested = (model['crop'] as { id?: string } | undefined)?.id;
    const flat = model['cropId'];
    const raw = nested ?? flat;
    if (raw != null && String(raw).trim() !== '') {
      this.factCropId = String(raw);
    }
  }

  private applyExtendedDto(ext: CropIdentificationExtendedDto): void {
    const model = this.formData as Record<string, unknown>;

    if (ext.fieldArea != null && !Number.isNaN(Number(ext.fieldArea))) {
      model['fieldArea'] = ext.fieldArea;
      this.fieldArea = Number(ext.fieldArea);
    }
    if (ext.cropId) {
      model['cropId'] = ext.cropId;
      this.factCropId = String(ext.cropId);
    } else if (ext.crop?.id) {
      this.factCropId = String(ext.crop.id);
      if (!model['cropId']) {
        model['cropId'] = ext.crop.id;
      }
    }
    const detectionRaw =
      ext.identificationDate ??
      (ext as { detectionDate?: string | null }).detectionDate;
    this.applyDetectionDate(model, detectionRaw);

    // Use topCandidates from DTO
    if (ext.topCandidates && ext.topCandidates.length > 0) {
      this.topCandidates = ext.topCandidates;
    }

    if (ext.fieldId) {
      model['fieldId'] = ext.fieldId;
    }
    if (ext.field) {
      model['field'] = ext.field;
    }
    if (ext.id) {
      model['aiResultId'] = ext.id;
    }

    const regionId = ext.region?.id ?? (ext as { regionId?: string }).regionId;
    if (regionId && !model['regionId']) {
      model['regionId'] = String(regionId);
    }

    if (ext.topProbability != null && !Number.isNaN(Number(ext.topProbability))) {
      model['probability'] = Math.round(Number(ext.topProbability) * 10000) / 100;
    }

    // Map AiInferenceImageDataSource enum to FieldSourceType for form display
    const mappedSource = this.mapAiSourceToForm(ext.aiInferenceImageDataSource);
    if (mappedSource) {
      model['source'] = mappedSource;
    }
  }

  private mapAiSourceToForm(source: AiInferenceImageDataSource | null | undefined): string | undefined {
    if (source == null) {
      return undefined;
    }
    if (source === AiInferenceImageDataSource.Satellite) {
      return FieldSourceType.Satellite;
    }
    if (source === AiInferenceImageDataSource.Uav) {
      return FieldSourceType.UAV;
    }
    return undefined;
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  polygonFillHex(): string {
    const cid = this.factCropId ?? (this.formData?.cropId as string | undefined);
    if (cid) {
      return this.cropCacheService.getCropColorById(String(cid), '#4287f5');
    }
    return (this.config?.data?.fillColor as string) || '#4287f5';
  }

  selectCandidateIndex(index: number): void {
    if (!this.topCandidates.length) {
      return;
    }
    const max = this.topCandidates.length - 1;
    const idx = Math.max(0, Math.min(index, max));
    this.selectedCandidateI.set(idx);
    const c = this.topCandidates[idx];
    const model = this.config.data.model as Record<string, unknown>;
    if (c?.id) {
      model['cropId'] = c.id;
      (this.formData as { cropId?: string }).cropId = c.id;
    }
    if (c && typeof c.score === 'number') {
      model['probability'] = Math.round(c.score * 10000) / 100;
    }
    this.cdr.markForCheck();
  }

  candidateScorePercent(index: number): string {
    const s = this.topCandidates[index]?.score;
    if (s === undefined || s === null) {
      return '-';
    }
    return `${(s * 100).toFixed(1)}%`;
  }

  /** Fallback when map did not provide `regionId` (uses model or click lat/lng). */
  private loadRegionByPoint(): void {
    const model = this.config.data.model as Record<string, unknown>;
    const lat = this.readCoordinate(model, 'lat');
    const lng = this.readCoordinate(model, 'lng');
    if (lat == null || lng == null) {
      return;
    }
    this.regionService
      .dictionariesRegionGetRegionByPointGet(lng, lat)
      .pipe(take(1), catchError(() => of(null)))
      .subscribe((region) => {
        if (region?.id) {
          (this.formData as { regionId?: string }).regionId = String(region.id);
          this.cdr.markForCheck();
        }
      });
  }

  private readCoordinate(model: Record<string, unknown>, axis: 'lat' | 'lng'): number | null {
    if (axis === 'lat') {
      const v = model['latitude'] ?? model['centerY'];
      const n = v != null ? Number(v) : NaN;
      return Number.isFinite(n) ? n : null;
    }
    const v = model['longitude'] ?? model['centerX'];
    const n = v != null ? Number(v) : NaN;
    return Number.isFinite(n) ? n : null;
  }

  /** Same resolution as `crop-identification-list` (`field` nested vs `fieldId`). */
  getFieldId(): string | null {
    const model = this.formData as Record<string, unknown>;
    const field = model['field'] as { id?: string } | undefined;
    const id = field?.id ?? model['fieldId'];
    return id != null && String(id).trim() !== '' ? String(id) : null;
  }

  getFieldName(): string | null {
    const model = this.formData as Record<string, unknown>;
    const field = model['field'] as { name?: string } | undefined;
    const name = field?.name ?? model['fieldName'] ?? model['title'];
    return name != null && String(name).trim() !== '' ? String(name) : null;
  }

  openCreateRequest(event?: Event): void {
    event?.stopPropagation();
    const fieldId = this.getFieldId();
    if (!fieldId) {
      return;
    }

    const activeCandidate = this.topCandidates[this.selectedCandidateI()];

    openCropIdentificationCreateRequestDialog(
      {
        fieldId,
        cropId: activeCandidate?.id ?? this.factCropId,
        preferredCrop: this.getCropFromCacheForCandidate(activeCandidate?.id),
        lat: this.readCoordinate(this.formData as Record<string, unknown>, 'lat'),
        lng: this.readCoordinate(this.formData as Record<string, unknown>, 'lng'),
      },
      {
        dialogService: this.dialogService,
        bidService: this.bidService,
        bidWorkTypeService: this.bidWorkTypeService,
        fieldService: this.fieldService,
        seasonService: this.seasonService,
        datePipe: this.datePipe,
        translate: this.translateService,
        appRef: this.appRef,
      },
    );
  }

  private getCropFromCacheForCandidate(candidateId: string | undefined): Crop | undefined {
    if (!candidateId) {
      return undefined;
    }
    return this.cropCacheService.cachedCropsData.find((crop) => crop.id === candidateId);
  }

  getEditForm(): IMsfFormBuilder[] {
    return [
      {
        name: 'cropId',
        title: 'Culture',
        type: 'select',
        selectService: this.cropCacheService,
        selectServiceMethod: 'getCrops',
        placeholder: 'Select',
        options: {
          col: 'col-4'
        }
      },
      {
        name: 'source',
        title: 'Source',
        type: 'select',
        placeholder: 'Select source',
        selectOptions: this.modalSourceOptions,
        options: {
          clearable: false,
          select: {optionLabel: 'name', translateBy: 'locale'},
          col: 'col-4'
        }
      },
      {
        name: 'fieldArea',
        title: 'Area Ga',
        type: 'input-number',
        placeholder: 'Enter',
        options: {
          col: 'col-4'
        },
      },
      {
        name: 'detectionDate',
        title: 'DETECTION_DATE',
        type: 'date',
        placeholder: 'DETECTION_DATE',
        options: {
          col: 'col-6'
        }
      },
      {
        name: 'probability',
        title: 'PROBABILITY_PERCENT',
        type: 'input',
        options: {
          col: 'col-6'
        }
      },
      {
        title: 'Latitude',
        name: 'latitude',
        type: 'input',
        options: {
          col: 'col-6',
        },
      },
      {
        title: 'Longitude',
        name: 'longitude',
        type: 'input',
        options: {
          col: 'col-6',
        },
      },
      {
        title: 'Region',
        name: 'regionId',
        type: 'tree-select',
        selectService: this.regionManagerService,
        selectServiceMethod: 'getRegionsObservable',
        options: {
          col: 'col-12',
          treeSelect: {
            treeBy: 'parentId',
          }
        },
        placeholder: 'Select region',
      },
      {
        name: 'comment',
        title: 'Comment',
        type: 'textarea',
        placeholder: 'Enter comment',
        options: {
          col: 'col-12',
        },
      },
    ];
  }
}
